// src/features/gatherings/utils/pendingEdit.ts
//
// Presentation helpers for host-submitted, staged gathering edits (ENG-856 /
// ENG-1097). Kept out of the components so the two rules that matter are
// testable in isolation:
//
//   1. An unrecognised pending-edit status never crashes and never becomes
//      actionable. ENG-1096 introduces `expired` and `superseded`; a build
//      compiled before that must degrade to a readable, non-actionable badge
//      rather than assume the three original statuses are exhaustive.
//   2. A field is proposed for change if and only if it is a key in the
//      pending edit's `changes` document, so the diff is always
//      `gathering[field]` → `changes[field]`.

import { getAssetBaseUrl } from '../../../utils/environment';
import {
  Gathering,
  GatheringPendingEdit,
  PENDING_EDIT_FIELDS,
  PendingEditField
} from '../../../types';

export type PendingEditStatusTone = 'warning' | 'success' | 'error' | 'default';

/**
 * Colours for one status tone, shared by every surface that renders a pending
 * edit (the review panel and the board card) so the two cannot disagree.
 *
 * Every value here is chosen to clear WCAG AA. `accent` is dark enough for
 * white 13px bold text at 4.5:1 (the earlier flat-UI palette sat at 2.19:1 for
 * `warning`, the tone admins see most), and is also the text and border colour
 * on `badgeBg` at 4.5:1+. Do not lighten these without re-measuring both pairs:
 *
 *   tone     accent     white on accent   accent on badgeBg
 *   warning  #8a5300    6.33:1            5.81:1  (on #fff4e0)
 *   success  #146c43    6.45:1            5.73:1  (on #e7f5ec)
 *   error    #b3261e    6.54:1            5.72:1  (on #fdecea)
 *   default  #4f5b5c    7.04:1            6.20:1  (on #eef1f2)
 *
 * The hues stay distinct (amber-brown / green / red / slate) on purpose, but
 * colour is never the only carrier: every surface renders `label` as text.
 */
export interface PendingEditTonePalette {
  /** Dark fill for a solid chip with white text; also border and icon colour. */
  accent: string;
  /** Near-invisible tint for a whole panel, keyed to the tone. */
  wash: string;
  /** Light tint for a small badge, with `accent` as its text and border. */
  badgeBg: string;
}

export const PENDING_EDIT_TONE_PALETTE: Record<
  PendingEditStatusTone,
  PendingEditTonePalette
> = {
  warning: {
    accent: '#8a5300',
    wash: 'rgba(138, 83, 0, 0.04)',
    badgeBg: '#fff4e0'
  },
  success: {
    accent: '#146c43',
    wash: 'rgba(20, 108, 67, 0.04)',
    badgeBg: '#e7f5ec'
  },
  error: {
    accent: '#b3261e',
    wash: 'rgba(179, 38, 30, 0.04)',
    badgeBg: '#fdecea'
  },
  default: {
    accent: '#4f5b5c',
    wash: 'rgba(79, 91, 92, 0.04)',
    badgeBg: '#eef1f2'
  }
};

export interface PendingEditStatusPresentation {
  /** The status exactly as the API sent it, for logging and debugging. */
  raw: string;
  label: string;
  tone: PendingEditStatusTone;
  /**
   * Panel heading for this status. Derived here rather than hardcoded at the
   * call site: a fixed "Awaiting Review" title contradicts the badge and the
   * description directly beneath it on every non-pending status.
   */
  heading: string;
  /** One-line explanation of what this status means for the admin. */
  description: string;
  /**
   * True only when the edit is still awaiting a decision. Approve/Reject are
   * gated on this, so an unknown status is inert by construction — we would
   * rather an admin refresh than have us POST a decision against a lifecycle
   * state this build does not understand.
   */
  isActionable: boolean;
  /** False when the API sent a status this build has no label for. */
  isRecognised: boolean;
}

/**
 * Human-readable form of an arbitrary status token, e.g. `needs_review` →
 * `Needs review`. Used only for statuses this build does not know about, so a
 * future backend value still reads as words rather than as a raw enum.
 */
const humaniseStatusToken = (raw: string): string => {
  const spaced = raw.replace(/[_-]+/g, ' ').trim();
  if (spaced === '') {
    return 'Unknown';
  }
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
};

/**
 * Maps a pending-edit status onto how it should render.
 *
 * The switch is deliberately defensive: every branch is explicit and the
 * `default` handles both statuses added by a newer backend and a missing or
 * malformed value. Do not replace this with a boolean `status === 'pending'`
 * check at a call site — the status carries information a boolean throws away
 * (an expired edit is not the same as a rejected one).
 */
export const describePendingEditStatus = (
  status: string | null | undefined
): PendingEditStatusPresentation => {
  const raw = typeof status === 'string' ? status : '';
  const normalised = raw.trim().toLowerCase();
  // A whitespace-only status carries no more information than a missing one, so
  // the two must present identically rather than one reading "Unknown status"
  // and the other "Unknown".
  const isBlank = normalised === '';

  switch (normalised) {
    case 'pending':
      return {
        raw,
        label: 'Pending review',
        tone: 'warning',
        heading: 'Host Changes Awaiting Review',
        description:
          'The host submitted these changes. Guests still see the live version until you approve them.',
        isActionable: true,
        isRecognised: true
      };
    case 'approved':
      return {
        raw,
        label: 'Approved',
        tone: 'success',
        heading: 'Host Changes Approved',
        description: 'These changes were approved and are live on the gathering.',
        isActionable: false,
        isRecognised: true
      };
    case 'rejected':
      return {
        raw,
        label: 'Rejected',
        tone: 'error',
        heading: 'Host Changes Rejected',
        description:
          'These changes were rejected and discarded. The live gathering was left untouched.',
        isActionable: false,
        isRecognised: true
      };
    case 'expired':
      // ENG-1096: staged edits hard-expire one day before the gathering starts.
      return {
        raw,
        label: 'Expired',
        tone: 'default',
        heading: 'Host Changes Expired',
        description:
          'These changes lapsed before they were reviewed and can no longer be applied. The live gathering is unchanged.',
        isActionable: false,
        isRecognised: true
      };
    case 'superseded':
      // ENG-1096: a newer submission replaced this one.
      return {
        raw,
        label: 'Superseded',
        tone: 'default',
        heading: 'Host Changes Superseded',
        description:
          'A newer submission from the host replaced these changes. Review the newer edit instead.',
        isActionable: false,
        isRecognised: true
      };
    default:
      return {
        raw,
        label: isBlank ? 'Unknown status' : humaniseStatusToken(raw),
        tone: 'default',
        heading: isBlank
          ? 'Host Changes: Unknown Status'
          : `Host Changes: ${humaniseStatusToken(raw)}`,
        description:
          'This dashboard does not recognise this review status, so it cannot offer an approve or reject action. Reload the page; if it persists, the API is ahead of the dashboard and adminfabrik needs updating.',
        isActionable: false,
        isRecognised: false
      };
  }
};

/** How a changed field's before/after values should be rendered. */
export type PendingEditFieldKind = 'image' | 'boolean' | 'longText' | 'text';

export interface PendingEditFieldDiff {
  /** Raw field name from the `changes` document. */
  field: string;
  label: string;
  kind: PendingEditFieldKind;
  /** Current live value on the gathering. */
  currentValue: unknown;
  /** Value the host proposed. */
  proposedValue: unknown;
  /** False for a field outside the server-side stageable whitelist. */
  isKnownField: boolean;
}

const FIELD_LABELS: Record<PendingEditField, string> = {
  title: 'Title',
  description: 'Description',
  image_object_key: 'Image',
  category: 'Category',
  community: 'Community',
  additional_notes: 'Additional notes',
  is_public: 'Public',
  is_members_only: 'Members only'
};

const FIELD_KINDS: Record<PendingEditField, PendingEditFieldKind> = {
  title: 'text',
  description: 'longText',
  image_object_key: 'image',
  category: 'text',
  community: 'text',
  additional_notes: 'longText',
  is_public: 'boolean',
  is_members_only: 'boolean'
};

const isKnownPendingEditField = (field: string): field is PendingEditField =>
  (PENDING_EDIT_FIELDS as readonly string[]).indexOf(field) !== -1;

/**
 * Best-effort rendering kind for a field the whitelist does not cover, so a
 * field added server-side still renders sensibly before this file catches up.
 */
const inferFieldKind = (value: unknown): PendingEditFieldKind => {
  if (typeof value === 'boolean') {
    return 'boolean';
  }
  if (typeof value === 'string' && value.length > 140) {
    return 'longText';
  }
  return 'text';
};

/**
 * Turns a pending edit into an ordered list of field diffs.
 *
 * Known whitelist fields come first in their declared order (so the panel's
 * layout is stable between edits), then anything unrecognised, alphabetically.
 */
export const derivePendingEditDiff = (
  gathering: Gathering,
  pendingEdit: GatheringPendingEdit | null | undefined
): PendingEditFieldDiff[] => {
  const changes = pendingEdit?.changes;
  if (!changes || typeof changes !== 'object') {
    return [];
  }

  const changedFields = Object.keys(changes);
  const known = PENDING_EDIT_FIELDS.filter(
    (field) => changedFields.indexOf(field) !== -1
  );
  const unknown = changedFields
    .filter((field) => !isKnownPendingEditField(field))
    .sort();

  const liveValues = gathering as unknown as Record<string, unknown>;

  return [...known, ...unknown].map((field) => {
    const proposedValue = changes[field];
    const isKnownField = isKnownPendingEditField(field);

    return {
      field,
      label: isKnownField ? FIELD_LABELS[field] : field,
      kind: isKnownField ? FIELD_KINDS[field] : inferFieldKind(proposedValue),
      currentValue: liveValues[field],
      proposedValue,
      isKnownField
    };
  });
};

/**
 * Recovers the asset base URL from a live `image_url`/`image_object_key` pair,
 * i.e. inverts the backend's `<base>/<object_key>` composition. Returns null
 * when the gathering has no live image, or when its URL is not of that shape
 * (a signed URL, say) and the base therefore cannot be read off it.
 */
const baseUrlFromLivePair = (gathering: Gathering): string | null => {
  const { image_url: liveUrl, image_object_key: liveKey } = gathering;
  if (!liveUrl || !liveKey) {
    return null;
  }

  const keySuffixIndex = liveUrl.lastIndexOf(liveKey);
  if (keySuffixIndex === -1 || keySuffixIndex + liveKey.length !== liveUrl.length) {
    // The live URL is not "<base><key>" — do not guess.
    return null;
  }

  return liveUrl.slice(0, keySuffixIndex);
};

/**
 * Builds a viewable URL for a proposed `image_object_key`.
 *
 * Two sources, in this order:
 *
 *   1. The live `image_url`/`image_object_key` pair on this gathering. This is
 *      authoritative — it is whatever the backend is serving right now, for
 *      this environment, so it cannot drift.
 *   2. `getAssetBaseUrl()`, the per-environment CDN constant. Needed because
 *      source 1 does not exist when the live gathering has no image, and an
 *      image change is the one field where showing the bare object key is not
 *      a review: the admin would be approving an image nobody has looked at.
 *
 * Returns null only when there is no proposed key at all. A resolved URL is
 * therefore not a promise that the image loads — callers MUST handle the <img>
 * failing (see PendingEditReviewPanel) and say plainly that the proposed image
 * cannot be previewed, rather than leaving a broken image in a diff row.
 */
export const resolveProposedImageUrl = (
  gathering: Gathering,
  proposedKey: unknown
): string | null => {
  if (typeof proposedKey !== 'string' || proposedKey === '') {
    return null;
  }

  const base = baseUrlFromLivePair(gathering) ?? `${getAssetBaseUrl()}/`;

  return `${base}${proposedKey.replace(/^\/+/, '')}`;
};

/** Renders a value for display, naming the empty cases explicitly. */
export const formatPendingEditValue = (
  value: unknown,
  kind: PendingEditFieldKind
): string => {
  if (kind === 'boolean') {
    if (typeof value !== 'boolean') {
      return value === null || value === undefined ? 'Not set' : String(value);
    }
    return value ? 'Yes' : 'No';
  }

  if (value === null || value === undefined) {
    return 'Not set';
  }
  if (typeof value === 'string') {
    return value.trim() === '' ? 'Empty' : value;
  }
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }
  return String(value);
};
